import { useCallback } from 'react';
import { useAuth } from '@clerk/react';
import { BookOpenIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { WithTooltip } from '../../components/WithTooltip';
import PromptLibraryInline from '../../components/PromptLibraryInline';
import { PROMPT_LIBRARY_TEMPLATES } from '../../constants/promptLibraryTemplates';
import { useLandingPageVariant } from '../../contexts/LandingPageVariantContext';
import { NAVBAR_HEIGHT_STYLE } from '../../utils/layout';
import { Mixpanel } from '../../services/Mixpanel';
import { DEFAULT_TITLE } from './constants';
import { LoadingScreen } from './components/LoadingScreen';
import { AttachmentChips } from './components/AttachmentChips';
import { AttachMenu } from './components/AttachMenu';
import {
  PromptBox,
  PROMPT_SEND_BUTTON_CLASSES,
  PROMPT_TEXTAREA_CLASSES,
} from './components/PromptBox';
import { AdaptMenu } from './components/AdaptMenu';
import { AlignMenu } from './components/AlignMenu';
import { SuggestionsPanel } from './components/SuggestionsPanel';
import { DebugComparePanel } from './components/DebugComparePanel';
import { PromptHistoryButton } from './components/PromptHistoryButton';
import { YoutubeTranscriptModal } from '../../components/modals/YoutubeTranscriptModal';
import { PasteLinkModal } from './components/PasteLinkModal';
import { ProjectQuotaInputNotice } from './components/ProjectQuotaInputNotice';
import { useCyclingPlaceholder } from './hooks/useCyclingPlaceholder';
import { useUnlimitedContext } from './contexts/UnlimitedContext';
import { UnlimitedModals, useUnlimitedModals } from './contexts/UnlimitedModalsContext';
import { useSidebarVisibility } from '../../contexts/SidebarVisibilityContext';
import Image from '../../components/Image';

/**
 * Main view of the Unlimited workshop — input area, toolbar, suggestions, and library.
 * Modals are rendered by UnlimitedModals (colocated with their state in UnlimitedModalsContext).
 */
export const UnlimitedMainView = () => {
  const {
    input,
    requireAuth,
    flags,
    book,
    attachments,
    autocomplete,
    handlePromptLibrarySelect,
    handlePromptHistorySelect,
  } = useUnlimitedContext();
  const modals = useUnlimitedModals();
  const { homeSidebarExpanded } = useSidebarVisibility();
  const { isSignedIn, isLoaded } = useAuth();
  const landingPageVariant = useLandingPageVariant();

  // Campaign landing pages swap the prompt library for the variant's templates.
  const promptTemplates = landingPageVariant?.prompt_templates?.length
    ? landingPageVariant.prompt_templates
    : PROMPT_LIBRARY_TEMPLATES;

  // Logged-out visitors only — the cycling prompts orient someone seeing the
  // workshop for the first time, and a signed-in teacher already knows what to
  // type. `isLoaded` is required alongside: Clerk reports isSignedIn as
  // undefined until it resolves, which would otherwise flash a few cycles at a
  // signed-in user on every load. On campaign landing pages the variant's
  // prompts cycle for everyone (no Clerk wait) unless the variant opts into a
  // static placeholder.
  const cyclingAllowed = landingPageVariant
    ? !landingPageVariant.static_placeholder
    : isLoaded && !isSignedIn;

  // Cycle prompt-library prompts through the placeholder while the box is still
  // empty. Anything in the input (text, a PDF, a book, a template) hands the
  // placeholder back to useInputUI, which has contextual copy for those states.
  //
  // `isDefaultPlaceholder` rather than `!hasInput` alone: hasInput ignores
  // attachedLinks entirely, and lags pdfFile/imageFiles until their S3 upload
  // resolves, so gating on it would leave prompts cycling over a contextual
  // placeholder. `query` is checked on top of hasInput because hasInput trims: a
  // lone space would hide a native placeholder but not the overlay span, which
  // would leave ghost text sitting on top of what the teacher typed.
  const cycling = useCyclingPlaceholder({
    basePlaceholder: input.inputPlaceholder,
    prompts: promptTemplates,
    enabled:
      cyclingAllowed &&
      input.isDefaultPlaceholder &&
      !input.query &&
      !input.hasInput &&
      !input.isLoading,
  });

  // ArrowRight on an empty box accepts the prompt currently on screen. Guarded
  // on an empty query so it never steals caret movement from real text.
  const handleKeyDown = (e) => {
    if (
      e.key === 'ArrowRight' &&
      !input.query &&
      cycling.activeTemplate &&
      !e.shiftKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey
    ) {
      e.preventDefault();
      handlePromptLibrarySelect(cycling.activeTemplate, 'placeholder');
      return;
    }
    autocomplete.handleKeyDown(e);
  };

  // Stable so the 21-tile library grid stays out of the animation's re-render path.
  const handleInlinePromptSelect = useCallback(
    (t) => handlePromptLibrarySelect(t, 'inline'),
    [handlePromptLibrarySelect]
  );

  return (
    <div className='flex bg-gray-50' style={{ minHeight: NAVBAR_HEIGHT_STYLE }}>
      <div
        id='unlimited-scroll-container'
        className='relative flex-1 flex flex-col items-center justify-start px-4 overflow-y-auto'
        style={{ maxHeight: NAVBAR_HEIGHT_STYLE }}>
        {/* Logo shown when the nav sidebar is collapsed — absolute so it scrolls away with
            the content (lives here, not in AppSidebar, so it sits inside this scroll container). */}
        {!homeSidebarExpanded && (
          <div className='absolute top-0 left-3 h-[72px] pt-[18px] hidden md:flex items-center z-[8]'>
            <a
              href='/'
              className='flex flex-col justify-center'
              onClick={() => Mixpanel.track('Header click', { item: 'Logo' })}>
              <Image className='h-12 w-auto ml-1' src='/logo.svg' alt='Diffit' />
            </a>
          </div>
        )}

        {input.isLoading && (
          <LoadingScreen text={input.isLoadingQuestions ? 'Thinking' : 'Generating'} />
        )}

        <div className='w-full max-w-3xl mt-[8vh] md:mt-[15.5vh] mb-[7vh]'>
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-light text-gray-900 mb-4'>
              {landingPageVariant?.headline ||
                flags.unlimitedInputCopyVariations?.title ||
                DEFAULT_TITLE}
            </h1>
          </div>

          <div className='relative'>
            <PromptBox
              top={
                <>
                  <AttachmentChips />

                  <input
                    ref={attachments.fileInputRef}
                    type='file'
                    accept='.pdf'
                    onChange={attachments.handleFileInputChange}
                    className='hidden'
                  />
                  <input
                    ref={attachments.imageInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/jpg,image/gif,image/webp'
                    onChange={attachments.handleImageInputChange}
                    className='hidden'
                  />
                </>
              }
              menus={
                <>
                  <AttachMenu />
                  <AdaptMenu />

                  {book.showTopLevelBooks && (
                    <WithTooltip
                      text='Choose a book'
                      position='bottom'
                      anchor='center'
                      size='xs'
                      showArrow>
                      <button
                        onClick={() =>
                          requireAuth(() => {
                            Mixpanel.track('Unlimited Books Button Clicked');
                            modals.setShowBookModal(true);
                          })
                        }
                        disabled={!!book.selectedBook}
                        className={`hidden md:flex items-center gap-1.5 py-2 px-3 rounded-xl transition-colors ${
                          book.selectedBook
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                            : 'text-gray-600 hover:bg-gray-100 border border-transparent'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        aria-label='Choose a book'>
                        <BookOpenIcon className='w-5 h-5' />
                        <span className='text-sm'>Books</span>
                      </button>
                    </WithTooltip>
                  )}

                  <AlignMenu />
                </>
              }
              actions={
                <>
                  <PromptHistoryButton
                    onSelect={handlePromptHistorySelect}
                    disabled={input.isLoading}
                    requireAuth={requireAuth}
                  />

                  <WithTooltip
                    text={!input.hasInput ? 'Enter a prompt to get started' : ''}
                    position='top'
                    anchor='center'
                    showArrow>
                    <button
                      onClick={() => {
                        if (!input.hasInput) {
                          input.inputRef.current?.focus();
                        } else {
                          input.handleSubmit();
                        }
                      }}
                      disabled={input.isLoading || attachments.isUploadingPdf}
                      className={PROMPT_SEND_BUTTON_CLASSES}
                      aria-label='Create resource'>
                      <ArrowRightIcon className='w-5 h-5' />
                    </button>
                  </WithTooltip>
                </>
              }>
              <div className='relative'>
                <textarea
                  ref={input.inputRef}
                  rows={1}
                  value={input.query}
                  onChange={autocomplete.handleInputChange}
                  onKeyDown={handleKeyDown}
                  onPaste={attachments.handlePaste}
                  disabled={input.isLoading}
                  // The base copy, not `cycling.text`: the textarea auto-sizes
                  // against its own placeholder, so tracking the animation stepped
                  // the box a line taller whenever a prompt wrapped and shoved the
                  // toolbar, suggestions and library down with it every few
                  // seconds. Still set while cycling, just transparent — the
                  // overlay below paints the visible, animatable prompt.
                  placeholder={input.inputPlaceholder}
                  // Keeps the accessible name explicit rather than inherited from
                  // whatever the placeholder happens to be.
                  aria-label={input.inputPlaceholder}
                  className={`${PROMPT_TEXTAREA_CLASSES} ${
                    cycling.active ? 'placeholder:text-transparent' : 'placeholder:text-gray-400'
                  }`}
                  autoComplete='off'
                />

                {/* Keyed by cycleKey so every prompt remounts and restarts the
                    animation already fully visible; animationend advances the cycle. */}
                {cycling.active && (
                  <span
                    key={cycling.cycleKey}
                    aria-hidden='true'
                    onAnimationEnd={cycling.advance}
                    className='animate-placeholder-cycle pointer-events-none absolute inset-x-0 top-0 truncate pt-6 px-6 text-lg text-gray-400'>
                    {cycling.text}
                  </span>
                )}
              </div>
            </PromptBox>

            {input.error && (
              <div className='mt-3 text-center text-red-600 text-sm'>
                <p>{input.error}</p>
              </div>
            )}

            <ProjectQuotaInputNotice />
          </div>

          <DebugComparePanel />
          <SuggestionsPanel />
        </div>

        <div id='prompt-library-section' className='w-full border-t border-gray-200 mb-10 mt-6' />
        <PromptLibraryInline
          onSelectPrompt={handleInlinePromptSelect}
          isLDidentified={flags.isLDidentified}
          templates={promptTemplates}
        />
        <div className='w-full border-t border-gray-200 mb-12 mt-16' />
      </div>

      {/* Modals with state in UnlimitedModalsContext */}
      <UnlimitedModals />

      {/* Attachment-coupled modals (state in useAttachments) */}
      <PasteLinkModal />
      <YoutubeTranscriptModal
        showModal={attachments.showYoutubeTranscriptModal}
        setShowModal={attachments.handleYoutubeModalClose}
        youtubeUrl={attachments.youtubeModalUrl}
        onUseTranscript={attachments.handleYoutubeTranscript}
      />
    </div>
  );
};
